import * as React from "react"
import { useEffect, useRef, useState } from "react"
import { f, clamp, TAU, useReducedMotion, usePointer, useFrame, smoothPointer } from "./motion"
import { sampleGlyph } from "./glyph"
import { Stage, type ExitLink } from "./NotFoundShell"
import { useHeroAnalytics } from "./analytics"
import "./notfound.css"

/**
 * The 404 artwork: iron filings on a magnet.
 *
 * Every mark is a short segment lying along a field. At rest the field is a
 * slow rotation with a spatial phase, so the plate breathes rather than moving
 * in lockstep; the cursor becomes the pole and the segments within reach swing
 * to face it. Chosen from four candidates (elastic sheet, shoal, ripple).
 *
 * Targets are sampled from the live font (./glyph), so the digits are the
 * site's own type rather than a traced path.
 */

const W = 640
const H = 320

/** Per-frame delta, clamped so returning to a background tab does not jump the field. */
const useClock = () => {
  const last = useRef(0)
  return (t: number) => {
    const dt = Math.min(0.05, Math.max(0, t - last.current))
    last.current = t
    return dt
  }
}

const useGlyph = (step: number, fontSize: number) => {
  // `pts` drives rendering; `ref` is what the frame loop reads. The loop is
  // created once, so it cannot see state updates - without the ref it draws
  // the initial empty array forever.
  const [pts, setPts] = useState<Array<[number, number]>>([])
  const ref = useRef<Array<[number, number]>>([])
  useEffect(() => {
    const sampled = sampleGlyph({ text: "404", width: W, height: H, step, fontSize, cx: 0.72, cy: 0.5 })
    ref.current = sampled
    setPts(sampled)
  }, [step, fontSize])
  return { pts, ref }
}

/* ================================================================= *
 * 1 · FILINGS
 * Every particle is a short segment that lies along a magnetic field.
 * The cursor is the pole: near it the segments swing to point at it,
 * far from it they settle back into a slow ambient drift. Dragging
 * across the digits combs them, which is the whole appeal - it is the
 * iron-filings-on-a-magnet feeling, and it never stops being nice.
 * ================================================================= */
export const FilingsPiece: React.FC<{ line?: string; links?: ExitLink[] }> = ({ line, links }) => {
  const reduce = useReducedMotion()
  const { ref: aRef } = useHeroAnalytics({ variant: "filings", surface: "404" })
  const ref = useRef<HTMLDivElement>(null)
  const p = usePointer(ref, reduce, { w: W, h: H })
  const segs = useRef<Array<SVGLineElement | null>>([])
  const { pts, ref: ptsRef } = useGlyph(9, 190)
  const ang = useRef<number[]>([])
  const dt = useClock()

  const draw = useRef((t: number) => {
    const d = dt(t)
    const q = smoothPointer(p.current)
    const P = ptsRef.current
    if (ang.current.length !== P.length) ang.current = P.map(() => 0)

    P.forEach(([x, y], i) => {
      const el = segs.current[i]
      if (!el) return
      const dx = q.x - x
      const dy = q.y - y
      const dist = Math.hypot(dx, dy)
      // pull falls off smoothly; without the smoothstep the boundary is visible
      const near = clamp(1 - dist / 150, 0, 1)
      const pull = near * near * (3 - 2 * near) * q.on
      // ambient field: a slow rotation with a spatial phase, so the whole
      // plate breathes instead of every segment moving in lockstep
      const amb = t * 0.35 + (x + y) * 0.006
      const target = pull > 0.01 ? Math.atan2(dy, dx) : amb
      // shortest-arc easing, or segments spin the long way round at the wrap
      let diff = target - ang.current[i]
      while (diff > Math.PI) diff -= TAU
      while (diff < -Math.PI) diff += TAU
      ang.current[i] += diff * Math.min(1, d * (3 + pull * 9))
      const a = ang.current[i]
      const len = 3.8 + pull * 3.4
      el.setAttribute("x1", f(x - Math.cos(a) * len))
      el.setAttribute("y1", f(y - Math.sin(a) * len))
      el.setAttribute("x2", f(x + Math.cos(a) * len))
      el.setAttribute("y2", f(y + Math.sin(a) * len))
      el.setAttribute("opacity", f(0.62 + pull * 0.38))
      el.setAttribute("stroke", pull > 0.35 ? "var(--color-text-accent)" : "var(--color-text-secondary)")
    })
  }).current
  useFrame(ref, draw, reduce)

  return (
    <div ref={aRef}>
      <Stage stageRef={ref} variant="filings" line={line} links={links}>
        <svg className="nf-svg" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
          {pts.map((_, i) => (
            <line key={i} ref={(el) => { segs.current[i] = el }}
              stroke="var(--color-text-secondary)" strokeWidth="1.9" strokeLinecap="round" />
          ))}
        </svg>
      </Stage>
    </div>
  )
}

